/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { ChevronLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCreateJob } from '../../../Hooks/useCreateJob';
import toast from 'react-hot-toast';
import { ZipAutoFillInput } from '../components/ZipAutoFillInput';

import { generateJobNumber } from '../../../utils/jobHelpers';

export default function CreateJobPage() {
    const navigate = useNavigate();
    const {
        surveyors,
        quotes,
        loadingSurveyors,
        loadingQuotes,
        createJob,
        isCreating,
    } = useCreateJob();

    const [jobNumber, setJobNumber] = useState('');
    const [totalVisits, setTotalVisits] = useState('1');
    const [isRecurring, setIsRecurring] = useState(false);
    const [repeats, setRepeats] = useState('Weekly');
    const [selectedQuoteId, setSelectedQuoteId] = useState('');
    const [selectedSurveyors, setSelectedSurveyors] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [visitInstructions, setVisitInstructions] = useState('');
    const [notes, setNotes] = useState('');
    const [location, setLocation] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [isBoatInWater, setIsBoatInWater] = useState(false);
    const [dock, setDock] = useState('');
    const [slipNumber, setSlipNumber] = useState('');
    const [dynamicFields, setDynamicFields] = useState([]);

    useEffect(() => {
        if (!jobNumber) {
            setJobNumber(generateJobNumber());
        }
    }, []);

    const handleSurveyorChange = (id, checked, amount) => {
        if (checked) {
            setSelectedSurveyors([{ id: parseInt(id), amount: parseFloat(amount) || 0 }]);
        } else {
            setSelectedSurveyors([]);
        }
    };

    const addDynamicField = () => {
        setDynamicFields(prev => [...prev, { id: Date.now(), key: '', value: '' }]);
    };

    const removeDynamicField = (id) => {
        setDynamicFields(prev => prev.filter(f => f.id !== id));
    };

    const updateDynamicField = (id, field, value) => {
        setDynamicFields(prev => prev.map(f => f.id === id ? { ...f, [field]: value } : f));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!jobNumber || !startDate || !startTime || selectedSurveyors.length === 0) {
            toast.error('Please fill all required fields');
            return;
        }

        if (isBoatInWater && (!dock || !slipNumber)) {
            toast.error('Dock and Slip Number are required when boat is in water');
            return;
        }

        const customFieldsObj = {};
        if (isBoatInWater) {
            dynamicFields.forEach(f => {
                if (f.key.trim()) {
                    customFieldsObj[f.key.trim()] = f.value.trim();
                }
            });
        }

        const payload = {
            quote_id: selectedQuoteId ? parseInt(selectedQuoteId) : null,
            job_number: jobNumber,
            total_visits: parseInt(totalVisits),
            start_date: startDate,
            start_time: startTime,
            end_time: endTime || null,
            repeats: isRecurring ? repeats : null,
            visit_instructions: visitInstructions || null,
            notes: notes || null,
            location: location || null,
            surveyors: selectedSurveyors,
            is_boat_in_water: isBoatInWater,
            dock: isBoatInWater ? dock : null,
            slip_number: isBoatInWater ? slipNumber : null,
            custom_fields: isBoatInWater ? customFieldsObj : null,
        };

        createJob(payload, {
            onSuccess: () => {
                navigate('/profile/jobs');
            },
        });
    };

    return (
        <section className="min-h-screen">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
                <div className="px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Link to="/profile/jobs" className="p-2 hover:bg-gray-100 rounded-lg transition">
                                <ChevronLeft className="w-5 h-5" />
                            </Link>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">New Job</h1>
                                <p className="text-xs text-gray-500">Create and schedule a new job</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="px-4 py-6 space-y-6">
                {/* Job Details */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-5">Job Details</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Job Number *</label>
                            <input
                                type="text"
                                value={jobNumber}
                                readOnly
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                                placeholder="Auto-generating..."
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Quote ID (optional)</label>
                            <select
                                value={selectedQuoteId}
                                onChange={(e) => setSelectedQuoteId(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                            >
                                <option value="">No quote selected</option>
                                {loadingQuotes ? (
                                    <option>Loading quotes...</option>
                                ) : (
                                    quotes.map(q => (
                                        <option key={q.id} value={q.id}>
                                            #{q.id} - {q.first_name} {q.last_name} ({q.service?.name})
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Total Visits</label>
                            <input
                                type="number"
                                min="1"
                                value={totalVisits}
                                onChange={(e) => setTotalVisits(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>

                        <div>
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
                                <input
                                    type="checkbox"
                                    checked={isRecurring}
                                    onChange={(e) => setIsRecurring(e.target.checked)}
                                    className="w-4 h-4 text-emerald-600 rounded"
                                />
                                Recurring Job
                            </label>
                            {isRecurring && (
                                <select
                                    value={repeats}
                                    onChange={(e) => setRepeats(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                                >
                                    <option>Weekly</option>
                                    <option>Bi-Weekly</option>
                                    <option>Monthly</option>
                                    <option>Quarterly</option>
                                </select>
                            )}
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Visit Instructions</label>
                            <textarea
                                rows={3}
                                value={visitInstructions}
                                onChange={(e) => setVisitInstructions(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                                placeholder="e.g. Please call the customer before arrival."
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isBoatInWater}
                                    onChange={(e) => setIsBoatInWater(e.target.checked)}
                                    className="w-4 h-4 text-emerald-600 rounded"
                                />
                                Is the boat in the water?
                            </label>
                        </div>

                        {isBoatInWater && (
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5 p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
                                <div className="md:col-span-2">
                                    <h3 className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Water Location Details</h3>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Dock Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={dock}
                                        onChange={(e) => setDock(e.target.value)}
                                        required={isBoatInWater}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                        placeholder="e.g. D-12"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Slip Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={slipNumber}
                                        onChange={(e) => setSlipNumber(e.target.value)}
                                        required={isBoatInWater}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                        placeholder="e.g. 402"
                                    />
                                </div>

                                <div className="md:col-span-2 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <label className="block text-xs font-medium text-gray-700">Additional Custom Fields</label>
                                        <button
                                            type="button"
                                            onClick={addDynamicField}
                                            className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 hover:text-emerald-700 transition"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                            Add Field
                                        </button>
                                    </div>

                                    {dynamicFields.map((field) => (
                                        <div key={field.id} className="flex gap-3 items-start animate-in slide-in-from-top-1 duration-200">
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    placeholder="Field Name (e.g. Gate Code)"
                                                    value={field.key}
                                                    onChange={(e) => updateDynamicField(field.id, 'key', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    placeholder="Value"
                                                    value={field.value}
                                                    onChange={(e) => updateDynamicField(field.id, 'value', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeDynamicField(field.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    {dynamicFields.length === 0 && (
                                        <p className="text-[11px] text-gray-400 italic">No additional custom fields added.</p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Notes</label>
                            <textarea
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                                placeholder="e.g. Urgent job, handle carefully."
                            />
                        </div>

                        <div className="md:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Zip Code</label>
                            <ZipAutoFillInput
                                zip={zipCode}
                                onZipChange={setZipCode}
                                onAddressResolved={({ city, state, country }) => {
                                    const parts = [city, state, country].filter(Boolean);
                                    if (parts.length > 0) {
                                        setLocation(parts.join(', '));
                                    }
                                }}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>

                        <div className="md:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Location</label>
                            <input
                                type="text"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                placeholder="e.g. Miami Beach Marina"
                            />
                        </div>
                    </div>
                </div>

                {/* Schedule */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-5">Schedule (First Visit)</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Date *</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Start Time *</label>
                            <input
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">End Time</label>
                            <input
                                type="time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-5">Assign Surveyor *</h2>
                    {loadingSurveyors ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {surveyors.map(s => {
                                const selected = selectedSurveyors.find(sel => sel.id === s.id);
                                return (
                                    <div
                                        key={s.id}
                                        className={`p-4 rounded-lg border-2 transition-all ${selected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="surveyor_selection"
                                                    checked={!!selected}
                                                    onChange={(e) => handleSurveyorChange(s.id, e.target.checked, selected?.amount || 0)}
                                                    className="w-5 h-5 text-emerald-600 focus:ring-emerald-500"
                                                />
                                                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                    {s.full_name.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{s.full_name}</p>
                                                    <p className="text-xs text-gray-500">{s.email} • {s.phone}</p>
                                                </div>
                                            </div>
                                        </div>
                                        {selected && (
                                            <div className="mt-3 ml-14">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount for this Surveyor ($)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={selected.amount}
                                                    onChange={(e) => {
                                                        const newAmount = parseFloat(e.target.value) || 0;
                                                        setSelectedSurveyors([{ id: s.id, amount: newAmount }]);
                                                    }}
                                                    className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    placeholder="e.g. 150.00"
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <Link
                        to="/profile/jobs"
                        className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={isCreating}
                        className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg font-bold shadow-md hover:shadow-lg disabled:opacity-70 flex items-center gap-2 transition"
                    >
                        {isCreating ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Creating...
                            </>
                        ) : (
                            'Create Job'
                        )}
                    </button>
                </div>
            </form>
        </section>
    );
}